import { inject, injectable } from 'inversify'
import { Group, Line, LineDashedMaterial } from 'three'
import { setup } from 'xstate'
import { Setting } from '@/plugins/cad/module/common/setting'
import { TYPES } from '@/plugins/cad/module/container/types'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { KeyCodes } from '@/shared/constants'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { ExtendEntityCommand } from '../command/ExtendEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { IPoint } from '../model/common/interface'
import type { IEntity } from '../model/entity'
import type { EntityDetector } from '../selector/entityDetector'
import type { EntitySelector } from '../selector/entitySelector'
import { type ExtendEndType, ExtendUtils } from './utils/extendUtils'

const EXTEND_DISABLED_MOUSE_TIP_ID = 'ExtendDisabledMouseTipId'

// =========================================================================
// 状态机定义（简单模式：idle → selectEntity，保持在 selectEntity 循环执行）
// =========================================================================

const extendEntityMachine = setup({
  actions: {
    entrySelectEntity: () => {},
  },
}).createMachine({
  id: 'extendEntity',
  initial: 'idle',
  states: {
    idle: {
      on: { NEXT: 'selectEntity' },
    },
    selectEntity: {
      entry: { type: 'entrySelectEntity' },
      // 保持在此状态，通过鼠标事件处理延伸逻辑
    },
  },
})

/**
 * 延伸编辑工具。
 *
 * 交互流程：
 *  1. 鼠标移动至实体端点附近 → 自动检测最近端并显示延伸预览（虚线）
 *  2. 鼠标左键单击 → 执行延伸，可继续对下一个对象延伸
 *  3. ESC → 退出工具
 */
@injectable()
export class ExtendEntityTool
  extends StateMachineTool<typeof extendEntityMachine>
  implements MouseEventInfoHandler, KeyEventInfoHandler
{
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.ExtendEntityTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.EntityDetector)
  private readonly entityDetector!: EntityDetector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  protected machine = extendEntityMachine.provide({
    actions: {
      entrySelectEntity: () => this.entrySelectEntity(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController

  /** 当前悬浮的实体 */
  private hoveredEntity?: IEntity
  /** 当前悬浮的端点类型 */
  private hoveredEndType?: ExtendEndType
  /** 当前计算出的延伸交点 */
  private hoveredIntersectionPt?: IPoint
  /** 当前鼠标世界坐标 */
  private currentMousePos?: IPoint

  /** Three.js 预览组 */
  private group: Group = new Group()
  /** 虚线预览对象 */
  private previewObject?: Line
  /** 预览虚线材质（复用） */
  private previewMaterial?: LineDashedMaterial

  private currentCommand?: ExtendEntityCommand

  // =========================================================================
  // 生命周期
  // =========================================================================

  protected onInitialize(): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.currentCommand = new ExtendEntityCommand(this.viewManager)
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.clearPreview()
    this.previewMaterial?.dispose()
    this.previewMaterial = undefined
    this.group.parent?.remove(this.group)
    this.entitySelector.clearSelection()
    this.mouseTipHelper.removeTip(MouseTipId.Edit)
    this.hideDisabledTip()
    this.eject(this.entityDetector)
    this.hoveredEntity = undefined
    this.hoveredEndType = undefined
    this.hoveredIntersectionPt = undefined
    this.currentMousePos = undefined
    this.currentCommand = undefined
  }

  // =========================================================================
  // 鼠标 / 键盘事件处理
  // =========================================================================

  /**
   * 鼠标按下：左键在命中可延伸端点时执行延伸。
   *
   * @param params - 鼠标事件参数
   */
  onMouseDown(params: MouseEventParams) {
    if (params.event.button !== 0) return
    if (!this.checkState('selectEntity')) return

    // 有效悬浮实体 + 有效交点 → 执行延伸
    if (this.hoveredEntity && this.hoveredEndType && this.hoveredIntersectionPt) {
      this.executeExtend()
    }
  }

  /**
   * 鼠标移动：实时更新悬浮实体与延伸预览。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (!this.checkState('selectEntity')) return

    this.hoveredEntity = this.entityDetector.getOneEntity() || undefined
    this.currentMousePos = { ...params.worldPos }
    this.updateHoverPreview()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  // =========================================================================
  // 状态机动作
  // =========================================================================

  private entrySelectEntity() {
    this.inject(this.entityDetector)
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.extend.target)
  }

  // =========================================================================
  // 预览更新
  // =========================================================================
  private getBoundaries(exclude: IEntity): IEntity[] {
    return this.viewManager
      .getVisibleEntities()
      .filter((e) => e !== exclude && ExtendUtils.BoundaryEntityTypes.has(e.type))
  }

  private updateHoverPreview() {
    if (!this.hoveredEntity || !this.currentMousePos) {
      this.clearPreview()
      this.clearHoverCache()
      this.hideDisabledTip()
      return
    }

    if (!ExtendUtils.isEntityExtendable(this.hoveredEntity)) {
      this.clearPreview()
      this.clearHoverCache()
      this.showDisabledTip()
      return
    }

    const endType = ExtendUtils.getNearestEnd(this.hoveredEntity, this.currentMousePos)
    if (!endType) {
      this.clearPreview()
      this.clearHoverCache()
      this.showDisabledTip()
      return
    }
    this.hoveredEndType = endType

    // 收集边界实体（除被延伸实体本身外的所有 BoundaryEntityTypes 实体）
    const boundaries = this.getBoundaries(this.hoveredEntity)
    const intersectionPt = ExtendUtils.findExtendPoint(this.hoveredEntity, endType, boundaries)
    this.hoveredIntersectionPt = intersectionPt ?? undefined

    if (!intersectionPt) {
      // 无可延伸边界 → 清除预览并提示
      this.clearPreview()
      this.showDisabledTip()
      return
    }

    // 计算预览路径
    const previewPts = ExtendUtils.getExtendPreviewPoints(
      this.hoveredEntity,
      endType,
      intersectionPt
    )
    if (previewPts.length < 2) {
      this.clearPreview()
      this.hoveredIntersectionPt = undefined
      this.showDisabledTip()
      return
    }

    this.hideDisabledTip()
    this.updatePreviewObject(previewPts)
  }

  private updatePreviewObject(points: IPoint[]) {
    const dashSize = this.controller.getPixelSizeInWorld(Setting.dashSize)

    if (!this.previewMaterial) {
      this.previewMaterial = new LineDashedMaterial({
        color: Setting.leadLineConfig.color,
        opacity: Setting.leadLineConfig.opacity,
        transparent: Setting.leadLineConfig.opacity < 1,
        dashSize,
        gapSize: dashSize / 2,
        depthTest: false,
        depthWrite: false,
      })
    }

    const geometry = DrawUtils.getBufferGeometryByPoints(points)

    if (!this.previewObject) {
      this.previewObject = new Line(geometry, this.previewMaterial)
      this.previewObject.computeLineDistances()
      this.group.add(this.previewObject)
    } else {
      this.previewObject.geometry.dispose()
      this.previewObject.geometry = geometry
      this.previewObject.computeLineDistances()
      this.previewObject.visible = true
    }
  }

  private clearPreview() {
    if (this.previewObject) {
      this.previewObject.visible = false
    }
  }

  private clearHoverCache() {
    this.hoveredEndType = undefined
    this.hoveredIntersectionPt = undefined
  }

  private showDisabledTip() {
    this.mouseTipHelper.addIconTip(EXTEND_DISABLED_MOUSE_TIP_ID, 'disabled')
  }

  private hideDisabledTip() {
    this.mouseTipHelper.removeTip(EXTEND_DISABLED_MOUSE_TIP_ID)
  }

  // =========================================================================
  // 执行延伸操作
  // =========================================================================

  private executeExtend() {
    if (!this.hoveredEntity || !this.hoveredEndType || !this.hoveredIntersectionPt) return

    // 执行延伸命令
    if (this.currentCommand) {
      this.currentCommand.addEntity(this.hoveredEntity, {
        endType: this.hoveredEndType,
        newPoint: this.hoveredIntersectionPt,
      })
    }

    // 执行后清除预览，但保留工具激活状态（等待下一次延伸）
    this.clearPreview()
    this.hoveredEntity = undefined
    this.hoveredEndType = undefined
    this.hoveredIntersectionPt = undefined
    this.hideDisabledTip()
    this.mouseTipHelper.updateTip(MouseTipId.Edit, CadTipKey.edit.extend.target)
  }
}
